import React, { useState, useEffect } from 'react';
import { StepProps, GalleryState } from './types';
import SectionInfoBox from './SectionInfoBox';

interface Step7Props extends StepProps {
    featuredPreview: string;
    setFeaturedImage: (file: File | null) => void;
    setFeaturedPreview: (url: string) => void;
    tpPreview: string;
    setTpImage: (file: File | null) => void;
    setTpPreview: (url: string) => void;
    galleryPreviews: GalleryState;
    galleryIcons: Record<string, string>;
    handleGalleryUpload: (album: string, files: File[]) => void;
    updateGalleryIcon: (album: string, icon: File | string) => void;
    removeGalleryImage: (album: string, index: number) => void;
    removeGalleryCategory: (album: string) => void;
    brochureFile: File | null;
    setBrochureFile: (file: File | null) => void;
    packageFeatures?: any;
    scoreSettings?: any;
}

const Step7Media: React.FC<Step7Props> = ({ 
    form, 
    handleChange, 
    featuredPreview, 
    setFeaturedImage, 
    setFeaturedPreview, 
    tpPreview,
    setTpImage,
    setTpPreview,
    galleryPreviews, 
    galleryIcons,
    handleGalleryUpload, 
    updateGalleryIcon,
    removeGalleryImage, 
    removeGalleryCategory,
    brochureFile, 
    setBrochureFile,
    errors,
    setForm,
    packageFeatures
}) => {
    const [activeTab, setActiveTab] = useState('exterior');
    const [brochureMode, setBrochureMode] = useState<'upload' | 'url'>(
        form.brochureUrl ? 'url' : 'upload'
    );

    useEffect(() => {
        if (form.brochureUrl && !brochureFile) {
            setBrochureMode('url');
        }
    }, [form.brochureUrl, brochureFile]);

    const tabs = [
        { id: 'exterior', label: 'Exterior', icon: 'fa-building' },
        { id: 'interior', label: 'Interior', icon: 'fa-couch' },
        { id: 'construction', label: 'Progress', icon: 'fa-helmet-safety' },
        { id: 'amenities', label: 'Amenities', icon: 'fa-gem' },
        { id: 'other', label: 'Other', icon: 'fa-images' }
    ];

    const topPhotos = galleryPreviews['topPhotos'] || [];
    const activeGallery = galleryPreviews[activeTab] || [];

    const totalGalleryPhotos = Object.entries(galleryPreviews).reduce((acc, [key, urls]) => {
        if (key === 'topPhotos') return acc;
        return acc + urls.length;
    }, 0) + topPhotos.length;

    const maxGalleryPhotos = packageFeatures === undefined ? 100 : (packageFeatures.galleryPhotos || 0);
    const canAddMorePhotos = totalGalleryPhotos < maxGalleryPhotos;

    const maxVirtualTours = packageFeatures === undefined ? 100 : (packageFeatures.virtualTours || 0);
    const canAddVirtualTour = (form.virtualTours?.length || 0) < maxVirtualTours;

    const maxYoutubeVideos = packageFeatures === undefined ? 100 : (packageFeatures.youtubeVideos || 0);
    const canAddYoutubeVideo = (form.youtubeVideos?.length || 0) < maxYoutubeVideos;

    return (
        <div className="space-y-8 bg-white p-2">
            {/* Header matches screenshot */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">7</div>
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">Project Gallery</h3>
                        <p className="text-sm text-slate-500">Add project images, videos and documents to showcase your project</p>
                    </div>
                </div>
                <button type="button" className="px-4 py-2 bg-white border border-blue-100 text-blue-600 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-blue-50 transition-colors">
                    <i className="fa-solid fa-eye"></i> Preview Gallery
                </button>
            </div>

            {/* Top Section: Main Cover Photo, Brochure & Top Photos */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Main Cover Photo */}
                <div className="md:col-span-3">
                    <div className="mb-2">
                        <label className="text-sm font-bold text-slate-800">Main Cover Photo <span className="text-red-500">*</span></label>
                    </div>
                    <label className={`w-full aspect-video rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${errors?.featuredImage ? 'border-red-500 bg-red-50' : 'border-blue-200 bg-blue-50/50 hover:bg-blue-50'} relative overflow-hidden`}>
                        {featuredPreview ? (
                            <>
                                <img src={featuredPreview} className="w-full h-full object-cover" alt="" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity">
                                    <span className="text-white text-sm font-semibold">Change Photo</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-3">
                                    <i className="fa-regular fa-image text-2xl text-slate-400"></i>
                                </div>
                                <p className="text-sm font-semibold text-slate-700">Upload Cover</p>
                                <p className="text-[10px] text-slate-400 mt-1">Recommended: 1920x1080</p>
                                <p className="text-[10px] text-slate-400">Max size: 5MB</p>
                            </>
                        )}
                        <input type="file" className="hidden" accept="image/*" onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) {
                                setFeaturedImage(file);
                                setFeaturedPreview(URL.createObjectURL(file));
                            }
                        }} />
                    </label>
                </div>

                {/* Brochure Upload OR URL */}
                <div className="md:col-span-3">
                    <div className="mb-2 flex items-center justify-between">
                        <label className="text-sm font-bold text-slate-800">Project Brochure</label>
                        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                            <button
                                type="button"
                                onClick={() => setBrochureMode('upload')}
                                className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all ${brochureMode === 'upload' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <i className="fa-solid fa-upload mr-1"></i>Upload
                            </button>
                            <button
                                type="button"
                                onClick={() => setBrochureMode('url')}
                                className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all ${brochureMode === 'url' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <i className="fa-solid fa-link mr-1"></i>URL
                            </button>
                        </div>
                    </div>

                    {brochureMode === 'upload' ? (
                        <label className="w-full aspect-video rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all border-blue-200 bg-blue-50/50 hover:bg-blue-50 relative overflow-hidden">
                            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-3">
                                <i className="fa-solid fa-file-pdf text-2xl text-red-500"></i>
                            </div>
                            <p className="text-sm font-semibold text-slate-700">Upload Brochure</p>
                            <p className="text-[10px] text-slate-400 mt-1">Format: PDF only</p>
                            <p className="text-[10px] text-slate-400">Max size: 10MB</p>
                            <input type="file" className="hidden" accept="application/pdf" onChange={e => {
                                const file = e.target.files?.[0];
                                if (file && setBrochureFile) {
                                    setBrochureFile(file);
                                    // Clear URL if switching to upload
                                    if (setForm) setForm(prev => ({ ...prev, brochureUrl: '' }));
                                }
                            }} />
                            {(brochureFile || form.brochureUrl) && (
                                <div className="absolute inset-x-0 bottom-0 py-2 bg-white/95 backdrop-blur-sm border-t border-slate-200 flex items-center justify-center">
                                    <p className="text-[10px] font-bold text-emerald-600 truncate px-2 text-center" title={brochureFile ? brochureFile.name : 'Brochure Uploaded'}>
                                        <i className="fa-solid fa-circle-check mr-1"></i> {brochureFile ? brochureFile.name : 'Brochure Uploaded'}
                                    </p>
                                </div>
                            )}
                        </label>
                    ) : (
                        <div className="w-full aspect-video rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 flex flex-col items-center justify-center gap-3 p-4">
                            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center">
                                <i className="fa-brands fa-google-drive text-2xl text-blue-500"></i>
                            </div>
                            <p className="text-sm font-semibold text-slate-700 text-center">Paste Brochure URL</p>
                            <p className="text-[10px] text-slate-400 text-center">Google Drive, Dropbox or direct PDF link</p>
                            <input
                                type="url"
                                className="w-full text-[11px] px-3 py-2 border border-blue-200 bg-white rounded-lg outline-none focus:border-blue-500 transition-colors placeholder:text-slate-400"
                                placeholder="https://drive.google.com/file/d/..."
                                value={form.brochureUrl || ''}
                                onClick={e => e.stopPropagation()}
                                onChange={e => {
                                    if (setForm) setForm(prev => ({ ...prev, brochureUrl: e.target.value }));
                                    if (setBrochureFile) setBrochureFile(null); // Clear file if URL is used
                                }}
                            />
                            {form.brochureUrl && (
                                <a
                                    href={form.brochureUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    className="text-[10px] font-bold text-blue-600 flex items-center gap-1 hover:underline"
                                >
                                    <i className="fa-solid fa-arrow-up-right-from-square"></i> Preview Link
                                </a>
                            )}
                        </div>
                    )}
                </div>

                {/* Top Photos */}
                <div className="md:col-span-6 flex flex-col">
                    <div className="mb-2">
                        <label className="text-sm font-bold text-slate-800">Top Photos (Gallery Preview)</label>
                    </div>
                    <div className="flex-1 flex flex-col">
                        <div className="flex gap-4 overflow-x-auto pb-2 items-center flex-1">
                            {topPhotos.slice(0, 4).map((url, idx) => (
                                <div key={idx} className="w-[180px] h-[130px] rounded-xl overflow-hidden shrink-0 relative group border border-slate-200 shadow-sm">
                                    <img src={url} className="w-full h-full object-cover" alt="" />
                                    <button 
                                        type="button"
                                        onClick={() => removeGalleryImage('topPhotos', idx)}
                                        className="absolute top-2 right-2 w-6 h-6 bg-white/90 text-red-500 rounded-full flex items-center justify-center transition-opacity shadow-sm hover:bg-white hover:text-red-600 border border-red-100"
                                    >
                                        <i className="fa-solid fa-trash-can text-xs"></i>
                                    </button>
                                </div>
                            ))}
                            {canAddMorePhotos ? (
                                <label className="w-[120px] h-[130px] rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center shrink-0 cursor-pointer hover:bg-slate-100 transition-colors">
                                    <i className="fa-solid fa-plus text-blue-600 text-xl mb-2"></i>
                                    <span className="text-xs font-semibold text-slate-600">Add More</span>
                                    <input type="file" multiple accept="image/*" className="hidden" onChange={e => handleGalleryUpload('topPhotos', Array.from(e.target.files || []))} />
                                </label>
                            ) : (
                                <div className="w-[120px] h-[130px] rounded-xl border-2 border-dashed border-red-200 bg-red-50 flex flex-col items-center justify-center shrink-0 opacity-70">
                                    <i className="fa-solid fa-lock text-red-500 text-xl mb-2"></i>
                                    <span className="text-[10px] font-bold text-red-600 text-center px-2">Limit Reached ({maxGalleryPhotos})</span>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-slate-500 text-center mt-2">You can add maximum {maxGalleryPhotos} gallery photos.</p>
                    </div>
                </div>
            </div>

            <hr className="border-slate-100" />

            {/* Gallery Categories */}
            <div>
                <h4 className="text-sm font-bold text-slate-800 mb-1">Gallery Categories</h4>
                <p className="text-xs text-slate-500 mb-4">Add images to different categories. You can upload multiple images in each category.</p>

                {/* Tabs */}
                <div className="flex items-center gap-2 border-b border-slate-200 mb-6 overflow-x-auto">
                    {tabs.map(tab => {
                        const isActive = activeTab === tab.id;
                        const count = galleryPreviews[tab.id]?.length || 0;
                        return (
                            <button 
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-5 py-3 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${isActive ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'}`}
                            >
                                <i className={`fa-solid ${tab.icon} ${isActive ? 'text-blue-600' : 'text-slate-400'}`}></i>
                                {tab.label} ({count})
                            </button>
                        );
                    })}
                </div>

                {/* Active Tab Content */}
                <div>
                    <h5 className="text-sm font-bold text-slate-800 mb-4">{tabs.find(t => t.id === activeTab)?.label} Images</h5>
                    <div className="flex flex-wrap gap-4 items-start">
                        {activeGallery.map((url, idx) => (
                            <div key={idx} className="w-[180px] h-[130px] rounded-xl overflow-hidden relative group border border-slate-200 shadow-sm">
                                <img src={url} className="w-full h-full object-cover" alt="" />
                                <button 
                                    type="button"
                                    onClick={() => removeGalleryImage(activeTab, idx)}
                                    className="absolute top-2 right-2 w-7 h-7 bg-white/90 text-red-500 rounded-full flex items-center justify-center transition-opacity shadow-sm hover:bg-white hover:text-red-600 border border-red-100"
                                >
                                    <i className="fa-solid fa-trash-can text-sm"></i>
                                </button>
                            </div>
                        ))}
                        {canAddMorePhotos ? (
                            <label className="w-[140px] h-[130px] rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center cursor-pointer hover:bg-slate-100 transition-colors">
                                <i className="fa-solid fa-plus text-slate-400 text-2xl mb-2"></i>
                                <span className="text-xs font-semibold text-slate-600">Add Images</span>
                                <input type="file" multiple accept="image/*" className="hidden" onChange={e => handleGalleryUpload(activeTab, Array.from(e.target.files || []))} />
                            </label>
                        ) : (
                            <div className="w-[140px] h-[130px] rounded-xl border-2 border-dashed border-red-200 bg-red-50 flex flex-col items-center justify-center opacity-70">
                                <i className="fa-solid fa-lock text-red-500 text-2xl mb-2"></i>
                                <span className="text-xs font-semibold text-red-600 text-center px-2">Limit Reached</span>
                            </div>
                        )}
                    </div>
                    
                    <div className="mt-6 text-xs text-slate-500">
                        <p className="font-semibold mb-1 text-slate-700">Upload Tips:</p>
                        <ul className="list-disc list-inside space-y-1 ml-1">
                            <li>Supported formats: JPG, PNG, WEBP</li>
                            <li>Recommended image size: 1920x1080px or higher</li>
                            <li>Maximum file size 2MB per image</li>
                        </ul>
                    </div>
                </div>
            </div>

            <hr className="border-slate-100" />

            {/* Video & Virtual Tour */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 360 Virtual Tour */}
                <div className="bg-white p-5 rounded-2xl border border-slate-200 flex flex-col shadow-sm h-full">
                    <div className="mb-4">
                        <h4 className="text-[14px] font-bold text-[#05144b] mb-1">360° Virtual Tour</h4>
                        <p className="text-[11px] text-slate-500 leading-snug">Add 360° virtual tour video of your project</p>
                    </div>
                    
                    <div className="w-full h-[110px] rounded-xl overflow-hidden mb-4 relative shrink-0">
                        <img src={featuredPreview || "https://placehold.co/600x300/e2e8f0/94a3b8?text=360+Preview"} className="w-full h-full object-cover" alt="" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <div className="w-14 h-14 border-[2px] border-white rounded-full flex items-center justify-center">
                                <span className="text-white text-[14px] font-bold flex items-center gap-1">360°</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-auto flex flex-col gap-3">
                        <div className="space-y-2">
                            {(form.virtualTours || []).map((tour, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <input type="text" className="flex-1 text-[11px] px-3 py-2.5 border border-slate-200 rounded-lg outline-none focus:border-blue-500 transition-colors" placeholder="URL..." value={tour.link} onChange={e => {
                                        if (setForm) {
                                            const next = [...(form.virtualTours || [])];
                                            next[idx] = { ...next[idx], link: e.target.value };
                                            setForm(prev => ({ ...prev, virtualTours: next }));
                                        }
                                    }} />
                                    {form.virtualTours.length > 1 && (
                                        <button type="button" onClick={() => setForm && setForm(prev => ({ ...prev, virtualTours: prev.virtualTours.filter((_, i) => i !== idx) }))} className="text-red-500 px-2 py-2 hover:bg-red-50 rounded-lg"><i className="fa-solid fa-trash-can"></i></button>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        {maxVirtualTours === 0 ? (
                            <div className="w-full py-2.5 bg-slate-50 border border-slate-200 text-slate-500 rounded-lg text-[12px] font-bold flex items-center justify-center gap-2 cursor-not-allowed">
                                <i className="fa-solid fa-lock"></i> Upgrade Package for 360° Tours
                            </div>
                        ) : canAddVirtualTour ? (
                            <button type="button" onClick={() => setForm && setForm(prev => ({ ...prev, virtualTours: [...(prev.virtualTours || []), { title: '360 Tour', link: '' }] }))} className="w-full py-2.5 bg-white border border-blue-100 text-blue-600 rounded-lg text-[12px] font-bold flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
                                <i className="fa-solid fa-plus"></i> Add 360° Virtual Tour ({form.virtualTours?.length || 0}/{maxVirtualTours})
                            </button>
                        ) : (
                            <div className="w-full py-2.5 bg-red-50 border border-red-200 text-red-500 rounded-lg text-[12px] font-bold flex items-center justify-center gap-2 cursor-not-allowed">
                                <i className="fa-solid fa-ban"></i> Max {maxVirtualTours} Tour(s) Reached
                            </div>
                        )}
                    </div>
                </div>

                {/* YouTube Video */}
                <div className="bg-white p-5 rounded-2xl border border-slate-200 flex flex-col shadow-sm h-full">
                    <div className="mb-4">
                        <h4 className="text-[14px] font-bold text-[#05144b] mb-1">YouTube Videos</h4>
                        <p className="text-[11px] text-slate-500 leading-snug">Add YouTube video links of your project</p>
                    </div>
                    
                    <div className="w-full h-[110px] rounded-xl overflow-hidden mb-4 relative group shrink-0">
                        {form.youtubeVideos?.[0]?.link ? (
                            <img src={`https://img.youtube.com/vi/${form.youtubeVideos[0].link.split('v=')[1]?.split('&')[0] || form.youtubeVideos[0].link.split('/').pop()}/maxresdefault.jpg`} className="w-full h-full object-cover" onError={(e) => (e.currentTarget.src = "https://placehold.co/600x300/e2e8f0/94a3b8?text=Video")} alt="" />
                        ) : (
                            <img src={featuredPreview || "https://placehold.co/600x300/e2e8f0/94a3b8?text=Video+Preview"} className="w-full h-full object-cover" alt="" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                            <div className="w-14 h-10 bg-[#FF0000] text-white rounded-[8px] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                <i className="fa-solid fa-play text-lg"></i>
                            </div>
                        </div>
                    </div>
                    
                    <div className="mt-auto flex flex-col gap-3">
                        <div className="space-y-2">
                            {(form.youtubeVideos || []).map((video, idx) => (
                                <div key={idx} className="flex flex-col gap-1.5 p-2 bg-slate-50 border border-slate-100 rounded-lg relative">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[9px] font-bold text-slate-400">Video #{idx + 1}</span>
                                        {form.youtubeVideos.length > 1 && (
                                            <button type="button" onClick={() => setForm && setForm(prev => ({ ...prev, youtubeVideos: prev.youtubeVideos.filter((_, i) => i !== idx) }))} className="text-red-500 px-1 hover:bg-red-50 rounded-md text-[9px] font-bold"><i className="fa-solid fa-trash-can"></i> Remove</button>
                                        )}
                                    </div>
                                    <input type="text" className="w-full text-[11px] px-2.5 py-1.5 border border-slate-200 bg-white rounded-md outline-none focus:border-blue-500 transition-colors" placeholder="Video Title (e.g. Walkthrough)" value={video.title} onChange={e => {
                                        if (setForm) {
                                            const next = [...(form.youtubeVideos || [])];
                                            next[idx] = { ...next[idx], title: e.target.value };
                                            setForm(prev => ({ ...prev, youtubeVideos: next }));
                                        }
                                    }} />
                                    <input type="text" className="w-full text-[11px] px-2.5 py-1.5 border border-slate-200 bg-white rounded-md outline-none focus:border-blue-500 transition-colors" placeholder="YouTube URL..." value={video.link} onChange={e => {
                                        if (setForm) {
                                            const next = [...(form.youtubeVideos || [])];
                                            next[idx] = { ...next[idx], link: e.target.value };
                                            setForm(prev => ({ ...prev, youtubeVideos: next }));
                                        }
                                    }} />
                                </div>
                            ))}
                        </div>
                        
                        {maxYoutubeVideos === 0 ? (
                            <div className="w-full py-2.5 bg-slate-50 border border-slate-200 text-slate-500 rounded-lg text-[12px] font-bold flex items-center justify-center gap-2 cursor-not-allowed">
                                <i className="fa-solid fa-lock"></i> Upgrade Package for YouTube Videos
                            </div>
                        ) : canAddYoutubeVideo ? (
                            <button type="button" onClick={() => setForm && setForm(prev => ({ ...prev, youtubeVideos: [...(prev.youtubeVideos || []), { title: '', link: '' }] }))} className="w-full py-2 bg-white border border-blue-100 text-blue-600 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-blue-50 transition-colors">
                                <i className="fa-solid fa-plus"></i> Add YouTube Video ({form.youtubeVideos?.length || 0}/{maxYoutubeVideos})
                            </button>
                        ) : (
                            <div className="w-full py-2.5 bg-red-50 border border-red-200 text-red-500 rounded-lg text-[12px] font-bold flex items-center justify-center gap-2 cursor-not-allowed">
                                <i className="fa-solid fa-ban"></i> Max {maxYoutubeVideos} Video(s) Reached
                            </div>
                        )}
                    </div>
                </div>
            </div>



        </div>
    );
};

export default Step7Media;

